import { css } from "@emotion/react";
import React, { forwardRef, useCallback, useId, useRef, useState } from "react";
import { FieldProps } from "../FieldBase/FieldProps";
import { FormControl } from "../FormControl";
import { FormHelperText } from "../FieldBase/FormHelperText";
import { InputLabel } from "../InputLabel";
import { ShimmerEffect } from "../ShimmerEffect";
import { useDitectInputTouched } from "../FieldBase/useDitectInputTouched";
import { useFieldGroup } from "../FieldGroup";
import { useMergeRefs } from "../hooks/useMergeRefs";
import { useTheme } from "../theme";
import { useValidation } from "../FieldBase/useValidation";

export interface FileFieldProps
  extends
    Omit<FieldProps<File[]>, "value" | "defaultValue">,
    Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      "value" | "onChange" | "defaultValue" | "type" | "placeholder"
    > {}

/**
 * ImageUploadAreaなどでは過剰になってしまうケースで気軽に使える、小さなファイル入力用のFieldです。
 */
export const FileField = forwardRef<HTMLDivElement, FileFieldProps>(
  (props, rootRef) => {
    const {
      label,
      style,
      className,
      onChange,
      onFocus,
      disabled,
      validationMessages,
      customValidations,
      helperText,
      invalid,
      loading: propsLoading,
      readOnly: propsReadonly,
      id,
      inputRef,
      onClick,
      ...restProps
    } = props;

    const theme = useTheme();
    const ref = useRef<HTMLInputElement>(null);
    const refs = useMergeRefs(inputRef, ref);
    const [files, setFiles] = useState<File[]>([]);
    const { loading, readOnly } = useFieldGroup({
      loading: propsLoading,
      readOnly: propsReadonly,
    });
    const validation = useValidation({
      ref,
      validationMessages,
      customValidations,
      value: files,
    });
    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.currentTarget.files ?? []);
        setFiles(files);
        if (onChange) {
          onChange(files, event);
        }
      },
      [onChange],
    );
    // input[type="file"]はreadOnly属性が機能しないため、クリックを抑止してファイル選択ダイアログを開かせない
    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLInputElement>) => {
        if (readOnly) {
          event.preventDefault();
          return;
        }
        if (onClick) {
          onClick(event);
        }
      },
      [onClick, readOnly],
    );
    const [touched, inputProps] = useDitectInputTouched(onFocus);

    const internalInputId = useId();
    const inputId = id ?? internalInputId;

    return (
      <FormControl style={style} className={className} ref={rootRef}>
        <InputLabel
          htmlFor={inputId}
          aria-disabled={disabled}
          required={props.required}
          loading={loading}
        >
          {label}
        </InputLabel>
        <ShimmerEffect active={loading}>
          <input
            id={inputId}
            disabled={disabled}
            readOnly={readOnly}
            aria-readonly={readOnly}
            type="file"
            onChange={handleChange}
            onClick={handleClick}
            data-invalid={!validation.valid}
            ref={refs}
            css={[
              theme.typography.body,
              css`
                box-sizing: border-box;
                width: 100%;
                padding: 8px;
                color: ${theme.palette.text.primary};
                cursor: pointer;
                background-color: white;
                border: 1px solid ${theme.palette.text.secondary};
                border-radius: 16px;
                transition: all 0.3s;
                &::file-selector-button {
                  padding: 12px 16px;
                  margin-inline-end: 16px;
                  font: inherit;
                  font-weight: bolder;
                  line-height: 24px;
                  color: ${theme.palette.intentions.primary.main};
                  cursor: pointer;
                  background-color: white;
                  border: 1px solid ${theme.palette.intentions.primary.main};
                  border-radius: 16px;
                  transition: background-color 0.3s;
                }
                &:not(
                    :disabled,
                    [aria-readonly="true"]
                  ):hover::file-selector-button {
                  background-color: ${theme.palette.intentions.primary.light};
                }
                &:focus-visible {
                  border-color: ${theme.palette.intentions.primary.main};
                  outline: none;
                }
                &[data-touched="true"][data-invalid="true"] {
                  border-color: ${theme.palette.intentions.error.main};
                }
                &[aria-readonly="true"] {
                  cursor: default;
                  background-color: ${theme.palette.action.readOnlyBackground};
                  border-color: ${theme.palette.action.readOnly};
                }
                &[aria-readonly="true"]::file-selector-button {
                  color: ${theme.palette.text.primary};
                  cursor: default;
                  background-color: transparent;
                  border-color: ${theme.palette.action.readOnly};
                }
                &:disabled {
                  color: ${theme.palette.text.disabled};
                  cursor: not-allowed;
                  background-color: ${theme.palette.action.disabledBackground};
                  border-color: ${theme.palette.action.disabled};
                }
                &:disabled::file-selector-button {
                  color: ${theme.palette.text.disabled};
                  cursor: not-allowed;
                  background-color: transparent;
                  border-color: ${theme.palette.action.disabled};
                }
              `,
            ]}
            {...restProps}
            {...inputProps}
          />
        </ShimmerEffect>
        <FormHelperText
          invalid={!validation.valid || invalid}
          loading={loading}
          touched={touched}
          value={files}
        >
          {
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            validation.message || helperText
          }
        </FormHelperText>
      </FormControl>
    );
  },
);
